import {
  BOOKING_TYPE_UNAVAILABLE,
  BRANCH_BOOKING_TYPES,
  BRANCH_UNAVAILABLE,
  NO_REGION,
  REASON_CATEGORY_UNAVAILABLE,
  REASON_UNAVAILABLE,
  REGION_UNAVAILABLE,
} from "../../features/Constants";
import {
  ActiveReasons,
  AvailableBranchReasons,
  AvailableBranches,
} from "../../models/BookingReasons";
import { Branch } from "../../models/Branches";
import { DropDownOption } from "../../models/DropDownOption";
import { AvailabilityStatus, BookingTypes } from "../../models/Enums";

export interface DisabledSelectOptions {
  reasons: ActiveReasons[];
  reasonBranches: {
    reason: ActiveReasons,
    regions: string[],
  }[];
  categories: string[];
  bookingTypes: string[];
}

interface RegionBranches {
  region: string;
  branches: Branch[];
}

interface CategoryReasons {
  category: string;
  reasons: ActiveReasons[];
}

interface BookingTypeCategories {
  bookingType: string;
  categories: string[];
}

// Gets all regions and their associated branches
export const getRegionsBranches = (branches: Branch[]): RegionBranches[] => {
  const regions: string[] = [];
  const regionBranches: RegionBranches[] = [];
  branches.forEach((branch: Branch) => {
    const found = regions.find(
      (region: string) => region === branch.BranchRegion
    );
    if (!found) {
      regions.push(branch.BranchRegion);
      regionBranches.push({ region: branch.BranchRegion, branches: [branch] });
    } else {
      const foundRegion = regionBranches.find(
        (region: RegionBranches) => region.region === branch.BranchRegion
      );
      if (foundRegion) {
        foundRegion.branches = [...foundRegion.branches, branch];
      }
    }
  });
  return regionBranches;
};

// Gets all categories and their associated reasons
export const getCategoryReasons = (
  reasons: ActiveReasons[]
): CategoryReasons[] => {
  const categories: string[] = [];
  const categoryReasons: CategoryReasons[] = [];
  reasons.forEach((reason: ActiveReasons) => {
    const found = categories.find(
      (category: string) => category === reason.ReasonCategory
    );
    if (!found) {
      categories.push(reason.ReasonCategory);
      categoryReasons.push({
        category: reason.ReasonCategory,
        reasons: [reason],
      });
    } else {
      const foundCategory = categoryReasons.find(
        (category: CategoryReasons) =>
          category.category === reason.ReasonCategory
      );
      if (foundCategory) {
        foundCategory.reasons = [...foundCategory.reasons, reason];
      }
    }
  });
  return categoryReasons;
};

// Gets all booking types and their associated categories
export const getBookingTypeCategories = (
  reasons: ActiveReasons[]
): BookingTypeCategories[] => {
  const bookingTypes: string[] = [];
  const bookingTypeCategories: BookingTypeCategories[] = [];
  reasons.forEach((reason: ActiveReasons) => {
    const found = bookingTypes.find(
      (type: string) => type === reason.ChannelType
    );
    if (!found) {
      bookingTypes.push(reason.ChannelType);
      bookingTypeCategories.push({
        bookingType: reason.ChannelType,
        categories: [reason.ReasonCategory],
      });
    } else {
      const foundType = bookingTypeCategories.find(
        (type: BookingTypeCategories) => type.bookingType === reason.ChannelType
      );
      if (foundType) {
        foundType.categories = [...foundType.categories, reason.ReasonCategory];
        foundType.categories = [...new Set(foundType.categories)];
      }
    }
  });
  return bookingTypeCategories;
};

export const getAvailableBranchesByReason = (
  reasonsAvailable: AvailableBranchReasons[],
  reasonPermutationID: number
): AvailableBranches[] => {
  const availableByReason = reasonsAvailable.filter(
    (branch: AvailableBranchReasons) =>
      Number(branch.ReasonPermutationID) === reasonPermutationID
  );

  const availableBranches: AvailableBranches[] = [];
  availableByReason.forEach((branch: AvailableBranchReasons) =>
    availableBranches.push(...branch.Branches)
  );
  return availableBranches;
};

export const filterBranchesBasedOnReason = (
  reasonPermutationID: number,
  reasonsAvailable: AvailableBranchReasons[],
  branches: Branch[]
): AvailableBranches[] => {
  const availableBranches = getAvailableBranchesByReason(
    reasonsAvailable,
    reasonPermutationID
  );

  const alteredBranches: AvailableBranches[] = [];
  branches.forEach((branch: Branch) => {
    const found = availableBranches.find(
      (item: AvailableBranches) => item.BranchCode === branch.BranchCode
    );
    if (found && found.AvailabilityStatus.toLowerCase() === "available") {
      alteredBranches.push(found);
    }
  });
  return alteredBranches;
};

const getRegionsToDisable = (
  reason: ActiveReasons,
  reasonsAvailable: AvailableBranchReasons[],
  branches: Branch[]
): string[] => {
  const disableRegions: string[] = [];
  const availableBranches = getAvailableBranchesByReason(
    reasonsAvailable,
    reason.ReasonPermutationID
  );
  const regionBranches: RegionBranches[] = getRegionsBranches(branches);
  regionBranches.forEach((region: RegionBranches) => {
    const allRegionBranches = availableBranches.filter(
      (branch: AvailableBranches) => {
        const found = region.branches.find(
          (b: Branch) => b.BranchCode === branch.BranchCode
        );
        return found;
      }
    );

    const available = allRegionBranches.filter(
      (branch: AvailableBranches) =>
        branch.AvailabilityStatus === AvailabilityStatus.Available
    );
    if (
      (allRegionBranches.length > 0 && available.length <= 0) ||
      (allRegionBranches.length <= 0 && (!available || available.length <= 0))
    ) {
      disableRegions.push(region.region);
    }
  });
  return disableRegions;
};

const getBookingTypesToDisable = (
  disabledReasonCategories: { category: string, bookingType: string }[],
  reasons: ActiveReasons[]
): string[] => {
  const disabledBookingTypes: string[] = [];
  const bookingTypeCategories: BookingTypeCategories[] =
    getBookingTypeCategories(reasons);

  bookingTypeCategories.forEach((type: BookingTypeCategories) => {
    const typeCategories = disabledReasonCategories
      .filter(
        (category: { category: string, bookingType: string }) =>
          category.bookingType === type.bookingType
      )
      .map((item: { category: string, bookingType: string }) => item.category);

    if (
      type.categories.sort().toString() === typeCategories.sort().toString() &&
      type.categories.length !== 0 &&
      typeCategories.length !== 0
    ) {
      disabledBookingTypes.push(type.bookingType);
    }
  });
  return disabledBookingTypes;
};

const getReasonCategoriesToDisable = (
  disabledReasons: ActiveReasons[],
  categoryReasons: CategoryReasons[],
  reasons: ActiveReasons[]
): { category: string, bookingType: string }[] => {
  const disableCategories: { category: string, bookingType: string }[] = [];

  const bookingTypeCategories: BookingTypeCategories[] =
    getBookingTypeCategories(reasons);

  categoryReasons.forEach((category: CategoryReasons) => {
    bookingTypeCategories.forEach((type: BookingTypeCategories) => {
      const filteredDisabledByType = disabledReasons.filter(
        (reason: ActiveReasons) =>
          reason.ChannelType === type.bookingType &&
          reason.ReasonCategory === category.category
      );
      const filteredByType = category.reasons.filter(
        (reason: ActiveReasons) => reason.ChannelType === type.bookingType
      );

      filteredDisabledByType.sort((a: ActiveReasons, b: ActiveReasons) => {
        return a.ReasonPermutationID < b.ReasonPermutationID ? -1 : 1;
      });

      filteredByType.sort((a: ActiveReasons, b: ActiveReasons) => {
        return a.ReasonPermutationID < b.ReasonPermutationID ? -1 : 1;
      });

      if (
        filteredDisabledByType.toString() === filteredByType.toString() &&
        filteredDisabledByType.length > 0 &&
        filteredByType.length > 0
      ) {
        disableCategories.push({
          category: category.category,
          bookingType: type.bookingType,
        });
      }
    });
  });
  return disableCategories;
};

export const formOptionsAvailabilityCheck = (
  reasons: ActiveReasons[],
  reasonsAvailable: AvailableBranchReasons[],
  branches: Branch[]
): DisabledSelectOptions => {
  const disabledReasons: ActiveReasons[] = [];
  const disabledReasonBranches: {
    reason: ActiveReasons,
    regions: string[],
  }[] = [];
  let disabledReasonCategories: { category: string, bookingType: string }[] =
    [];
  let disabledBookingTypes: string[] = [];

  reasons.forEach((reason: ActiveReasons) => {
    const filteredBranches = filterBranchesBasedOnReason(
      reason.ReasonPermutationID,
      reasonsAvailable,
      branches
    );

    if (filteredBranches.length > 0) {
      const disableRegions = [
        ...getRegionsToDisable(reason, reasonsAvailable, branches),
      ];
      disabledReasonBranches.push({
        reason,
        regions: disableRegions,
      });
    } else {
      disabledReasons.push(reason);
    }
  });

  if (disabledReasons.length > 0) {
    const categoryReasons: CategoryReasons[] = getCategoryReasons(reasons);
    disabledReasonCategories = [
      ...getReasonCategoriesToDisable(
        disabledReasons,
        categoryReasons,
        reasons
      ),
    ];

    disabledBookingTypes = [
      ...getBookingTypesToDisable(disabledReasonCategories, reasons),
    ];
  }

  const allDisabledCategories: string[] = disabledReasonCategories.map(
    (item: { category: string, bookingType: string }) => item.category
  );
  return {
    categories: allDisabledCategories,
    reasons: disabledReasons,
    bookingTypes: disabledBookingTypes,
    reasonBranches: disabledReasonBranches,
  };
};

export const setDisabledBookingTypeOptions = (
  options: DropDownOption[],
  types: string[],
  filteredTypes: string[],
  region: any
): DropDownOption[] => {
  const disabledTypes: DropDownOption[] = [];
  const enabledTypes: DropDownOption[] = [];

  if (!region || region === "" || region === NO_REGION) {
    options.forEach((type: DropDownOption) => {
      const found = filteredTypes.find(
        (option: string) => option === type.value
      );

      const foundType = types.find(
        (item: string) => item.toLowerCase() === type.value.toLowerCase()
      );

      const walkIn =
        foundType &&
        type.value.toLowerCase() === BookingTypes.WalkIn.toLowerCase();
      const vat =
        foundType &&
        type.value.toLowerCase() === BookingTypes.VATRegistration.toLowerCase();
      const popUp =
        type.value.toLowerCase() === BookingTypes.PopUp.toLowerCase() &&
        found?.toLowerCase() === BookingTypes.PopUp.toLowerCase();
      const mtu =
        type.value.toLowerCase() === BookingTypes.Mobile.toLowerCase() &&
        found?.toLowerCase() === BookingTypes.Mobile.toLowerCase();

      if (walkIn || vat) {
        const disabledType = { ...type };
        disabledType.disabled = true;
        disabledType.description =
          type.description + ` ${BOOKING_TYPE_UNAVAILABLE}`;
        disabledTypes.push(disabledType);
      } else if (
        popUp ||
        mtu ||
        found?.toLowerCase() === BookingTypes.WalkIn.toLowerCase() ||
        found?.toLowerCase() === BookingTypes.VATRegistration.toLowerCase()
      ) {
        enabledTypes.push(type);
      } else {
        const disabledType = { ...type };
        disabledType.disabled = true;
        disabledType.description =
          type.description + ` ${BOOKING_TYPE_UNAVAILABLE}`;
        disabledTypes.push(disabledType);
      }
    });
  } else {
    options.forEach((type: DropDownOption) => {
      const found = filteredTypes.find(
        (option: string) => option === type.value
      );
      const availableFound = types.find(
        (option: string) => option === type.value
      );

      if (
        (found &&
          BRANCH_BOOKING_TYPES.includes(found.toLowerCase()) &&
          !availableFound) ||
        (!found && !availableFound)
      ) {
        enabledTypes.push(type);
      } else if (found) {
        const disabledType = { ...type };
        disabledType.disabled = true;
        disabledType.description =
          type.description + ` ${BOOKING_TYPE_UNAVAILABLE}`;
        disabledTypes.push(disabledType);
      }
    });
  }

  disabledTypes.sort((a: DropDownOption, b: DropDownOption) => {
    return a.value.toLowerCase() < b.value.toLowerCase() ? -1 : 1;
  });
  enabledTypes.sort((a: DropDownOption, b: DropDownOption) => {
    return a.value.toLowerCase() < b.value.toLowerCase() ? -1 : 1;
  });

  const index = enabledTypes.findIndex(
    (type: DropDownOption) =>
      type.value?.toLowerCase() === BookingTypes.WalkIn.toLowerCase()
  );
  if (index > -1) {
    enabledTypes.push(enabledTypes.splice(index, 1)[0]);
  }

  const vatIndex = enabledTypes.findIndex(
    (type: DropDownOption) =>
      type.value?.toLowerCase() === BookingTypes.VATRegistration.toLowerCase()
  );
  if (vatIndex > -1) {
    enabledTypes.push(enabledTypes.splice(vatIndex, 1)[0]);
  }

  return [...enabledTypes, ...disabledTypes];
};

export const setDisabledCategoryOptions = (
  filteredActiveReasons: ActiveReasons[],
  reasonsAvailable: AvailableBranchReasons[],
  options: DropDownOption[],
  disabledOptions: DisabledSelectOptions | null,
  allActiveReasons: ActiveReasons[],
  type: string,
  reasonsOptions: DropDownOption[]
): DropDownOption[] => {
  const disabledCategories: DropDownOption[] = [];
  const enabledCategories: DropDownOption[] = [];

  const disabledReasons = setDisabledReasonOptions(
    filteredActiveReasons,
    reasonsOptions,
    disabledOptions?.reasons ?? []
  );

  filteredActiveReasons.forEach((reason: ActiveReasons) => {
    const found = options.find(
      (option: DropDownOption) => option.value === reason.ReasonCategory
    );
    if (found) {
      const filteredReasons = allActiveReasons.filter(
        (reason: ActiveReasons) =>
          reason.ReasonCategory === found.value && reason.ChannelType === type
      );

      const reasons: DropDownOption[] = [];
      filteredReasons.forEach((reason: ActiveReasons) => {
        const foundReason = disabledReasons.find(
          (item: DropDownOption) =>
            item.value === reason.ReasonPermutationID.toString()
        );

        const availableBranchesForReason = getAvailableBranchesByReason(
          reasonsAvailable,
          reason.ReasonPermutationID
        ).filter(
          (branch: AvailableBranches) =>
            branch.AvailabilityStatus === AvailabilityStatus.Available
        );

        if (foundReason && availableBranchesForReason.length <= 0) {
          reasons.push(foundReason);
        }
      });

      if (
        disabledOptions?.categories.includes(found.value) &&
        filteredReasons.length === reasons.length
      ) {
        const disabledCategory = { ...found };
        disabledCategory.disabled = true;
        disabledCategory.description =
          disabledCategory.description + ` ${REASON_CATEGORY_UNAVAILABLE}`;
        disabledCategories.push(disabledCategory);
      } else {
        enabledCategories.push(found);
      }
    }
  });

  disabledCategories.sort((a: DropDownOption, b: DropDownOption) => {
    return a.value.toLowerCase() < b.value.toLowerCase() ? -1 : 1;
  });
  enabledCategories.sort((a: DropDownOption, b: DropDownOption) => {
    return a.value.toLowerCase() < b.value.toLowerCase() ? -1 : 1;
  });

  const disabledCategoriesNoDuplicates = [
    ...new Map(
      disabledCategories.map((option: DropDownOption) => [option.value, option])
    ).values(),
  ];
  const enabledCategoriesNoDuplicates = [
    ...new Map(
      enabledCategories.map((option: DropDownOption) => [option.value, option])
    ).values(),
  ];

  const index = enabledCategoriesNoDuplicates.findIndex(
    (category: DropDownOption) => category.value.toLowerCase() === "other"
  );
  if (index > -1) {
    enabledCategoriesNoDuplicates.push(
      enabledCategoriesNoDuplicates.splice(index, 1)[0]
    );
  }

  return [...enabledCategoriesNoDuplicates, ...disabledCategoriesNoDuplicates];
};

export const setAllDisabledRegionOptions = (
  reasonBranches: {
    reason: ActiveReasons,
    regions: string[],
  }[],
  regionsOptions: DropDownOption[],
  type: string
): DropDownOption[] => {
  if (reasonBranches && reasonBranches.length > 0) {
    const filteredByType = reasonBranches.filter(
      (branch: { reason: ActiveReasons, regions: string[] }) =>
        branch.reason.ChannelType === type
    );

    let filteredByLength = null;
    if (filteredByType && filteredByType.length > 0) {
      filteredByLength = filteredByType.reduce((prev, next) =>
        prev.regions.length > next.regions.length ? next : prev
      );
    }

    const disabledRegions = setDisabledRegionOptions(
      reasonBranches ?? [],
      filteredByLength?.reason?.ReasonPermutationID?.toString() ?? null,
      regionsOptions
    );
    return disabledRegions;
  }
  return regionsOptions;
};

export const setAllDisabledBranchOptions = (
  reasonBranches: {
    reason: ActiveReasons,
    regions: string[],
  }[],
  options: DropDownOption[],
  type: string,
  reasonsAvailable: AvailableBranchReasons[]
): DropDownOption[] => {
  if (reasonBranches && reasonBranches.length > 0) {
    const filteredByType = reasonBranches.filter(
      (branch: { reason: ActiveReasons, regions: string[] }) =>
        branch.reason.ChannelType === type
    );

    let availableBranchCodes: string[] = [];
    filteredByType.forEach(
      (type: { reason: ActiveReasons, regions: string[] }) => {
        const found = reasonsAvailable.find(
          (available: AvailableBranchReasons) =>
            available.ReasonPermutationID === type.reason.ReasonPermutationID
        );
        if (found) {
          const available = found.Branches.filter(
            (branch: AvailableBranches) =>
              branch.AvailabilityStatus === AvailabilityStatus.Available
          ).map((branch: AvailableBranches) => branch.BranchCode);
          availableBranchCodes = [...availableBranchCodes, ...available];
          availableBranchCodes = [...new Set(availableBranchCodes)];
        }
      }
    );

    const disabledBranches: DropDownOption[] = [];
    const enabledBranches: DropDownOption[] = [];
    options.forEach((option: DropDownOption) => {
      if (availableBranchCodes.includes(option.value)) {
        enabledBranches.push(option);
      } else {
        const branch = { ...option };
        branch.disabled = true;
        branch.description = branch.description + ` ${BRANCH_UNAVAILABLE}`;
        disabledBranches.push(branch);
      }
    });

    disabledBranches.sort((a: DropDownOption, b: DropDownOption) => {
      return a.description.toLowerCase() < b.description.toLowerCase() ? -1 : 1;
    });
    enabledBranches.sort((a: DropDownOption, b: DropDownOption) => {
      return a.description.toLowerCase() < b.description.toLowerCase() ? -1 : 1;
    });
    return [...enabledBranches, ...disabledBranches];
  }
  return options;
};

export const setDisabledRegionOptions = (
  reasonBranches: {
    reason: ActiveReasons,
    regions: string[],
  }[],
  reason: string | null,
  options: DropDownOption[]
): DropDownOption[] => {
  if (reasonBranches && reasonBranches.length > 0) {
    const found = reasonBranches.find(
      (option: { reason: ActiveReasons, regions: string[] }) =>
        option.reason.ReasonPermutationID.toString() === reason
    );

    if (found) {
      if (found.regions.length > 0) {
        const disabledRegions: DropDownOption[] = [];
        const enabledRegions: DropDownOption[] = [];
        options.forEach((region: DropDownOption) => {
          if (found.regions.includes(region.value)) {
            const option = { ...region };
            option.disabled = true;
            option.description = option.description + ` ${REGION_UNAVAILABLE}`;
            disabledRegions.push(option);
          } else {
            enabledRegions.push(region);
          }
        });

        disabledRegions.sort((a: DropDownOption, b: DropDownOption) => {
          return a.value.toLowerCase() < b.value.toLowerCase() ? -1 : 1;
        });
        enabledRegions.sort((a: DropDownOption, b: DropDownOption) => {
          return a.value.toLowerCase() < b.value.toLowerCase() ? -1 : 1;
        });
        return [...enabledRegions, ...disabledRegions];
      }
    }
  }
  return options;
};

export const setDisabledReasonOptions = (
  filteredActiveReasons: ActiveReasons[],
  options: DropDownOption[],
  disabledReasons: ActiveReasons[]
): DropDownOption[] => {
  const reasons: DropDownOption[] = [];
  const enabledReasons: DropDownOption[] = [];
  filteredActiveReasons.forEach((reason: ActiveReasons) => {
    const found = options.find(
      (option: DropDownOption) =>
        option.value === reason.ReasonPermutationID.toString()
    );
    if (found) {
      const disableReason = disabledReasons.find(
        (option: ActiveReasons) =>
          option.ReasonPermutationID.toString() === found.value
      );
      if (disableReason) {
        const disabledReason = { ...found };
        disabledReason.disabled = true;
        disabledReason.description =
          disabledReason.description + ` ${REASON_UNAVAILABLE}`;
        reasons.push(disabledReason);
      } else {
        enabledReasons.push(found);
      }
    }
  });

  reasons.sort((a: DropDownOption, b: DropDownOption) => {
    return a.description.toLowerCase() < b.description.toLowerCase() ? -1 : 1;
  });
  enabledReasons.sort((a: DropDownOption, b: DropDownOption) => {
    return a.description.toLowerCase() < b.description.toLowerCase() ? -1 : 1;
  });
  return [...enabledReasons, ...reasons];
};

export const setDisabledReasonForCategoryOptions = (
  filteredActiveReasons: ActiveReasons[],
  reasonsAvailable: AvailableBranchReasons[],
  branch: string
): DropDownOption[] => {
  const disabledReasons: DropDownOption[] = [];
  const enabledReasons: DropDownOption[] = [];
  filteredActiveReasons.forEach((reason: ActiveReasons) => {
    const found = reasonsAvailable.find(
      (available: AvailableBranchReasons) =>
        available.ReasonPermutationID === reason.ReasonPermutationID &&
        (branch === "" ||
          available.Branches.find(
            (item: AvailableBranches) => item.BranchCode === branch
          ))
    );

    if (found) {
      enabledReasons.push({
        set: "reasons",
        value: reason.ReasonPermutationID.toString(),
        description: reason.ReasonDescription,
        seq: 1,
      });
    } else {
      disabledReasons.push({
        set: "reasons",
        value: reason.ReasonPermutationID.toString(),
        description: reason.ReasonDescription + ` ${REASON_UNAVAILABLE}`,
        seq: 1,
        disabled: true,
      });
    }
  });

  disabledReasons.sort((a: DropDownOption, b: DropDownOption) => {
    return a.value.toLowerCase() < b.value.toLowerCase() ? -1 : 1;
  });

  enabledReasons.sort((a: DropDownOption, b: DropDownOption) => {
    return a.value.toLowerCase() < b.value.toLowerCase() ? -1 : 1;
  });

  return [...enabledReasons, ...disabledReasons];
};

export const setDisabledBranchOptions = (
  reasonsAvailable: AvailableBranchReasons[],
  options: DropDownOption[],
  reasonPermutationID: number
): DropDownOption[] => {
  const availableByReason = reasonsAvailable.filter(
    (branch: AvailableBranchReasons) =>
      Number(branch.ReasonPermutationID) === reasonPermutationID
  );

  const availableBranches: AvailableBranches[] = [];
  availableByReason.forEach((branch: AvailableBranchReasons) =>
    availableBranches.push(...branch.Branches)
  );

  const disabledBranches: DropDownOption[] = [];
  const enabledBranches: DropDownOption[] = [];
  options.forEach((branch: DropDownOption) => {
    const index = availableBranches.findIndex(
      (item: AvailableBranches) => item.BranchCode === branch.value
    );
    if (index > -1) {
      enabledBranches.push(branch);
    } else {
      branch.disabled = true;
      branch.description = branch.description + ` ${BRANCH_UNAVAILABLE}`;
      disabledBranches.push(branch);
    }
  });

  disabledBranches.sort((a: DropDownOption, b: DropDownOption) => {
    return a.description.toLowerCase() < b.description.toLowerCase() ? -1 : 1;
  });
  enabledBranches.sort((a: DropDownOption, b: DropDownOption) => {
    return a.description.toLowerCase() < b.description.toLowerCase() ? -1 : 1;
  });
  return [...enabledBranches, ...disabledBranches];
};

export const filterReasonCategoriesByBranch = (
  branch: string,
  reasonsAvailable: AvailableBranchReasons[],
  allActiveReasons: ActiveReasons[],
  type: string
) => {
  const filteredByBranch = reasonsAvailable.filter(
    (reason: AvailableBranchReasons) =>
      reason.Branches.find(
        (item: AvailableBranches) => item.BranchCode === branch
      )
  );

  const reasons: number[] = [];
  filteredByBranch.forEach((reason: AvailableBranchReasons) => {
    const found = allActiveReasons.find(
      (active: ActiveReasons) =>
        active.ReasonPermutationID === reason.ReasonPermutationID &&
        active.ChannelType === type
    );
    if (found) {
      reasons.push(found.ReasonPermutationID);
    }
  });

  const filteredByType = allActiveReasons.filter(
    (active: ActiveReasons) => active.ChannelType === type
  );

  const disabledCategories: DropDownOption[] = [];
  const enabledCategories: DropDownOption[] = [];
  filteredByType.forEach((reason: ActiveReasons) => {
    const found = enabledCategories.find(
      (option: DropDownOption) => option.value === reason.ReasonCategory
    );

    if (
      reasons.includes(reason.ReasonPermutationID) ||
      (found && enabledCategories.length <= 0) ||
      (!reasons.includes(reason.ReasonPermutationID) && found)
    ) {
      enabledCategories.push({
        set: "reasonCategories",
        value: reason.ReasonCategory,
        description: reason.ReasonCategory,
        seq: 1,
      });
    } else {
      disabledCategories.push({
        set: "reasonCategories",
        value: reason.ReasonCategory,
        description: reason.ReasonCategory,
        seq: 1,
      });
    }
  });

  disabledCategories.sort((a: DropDownOption, b: DropDownOption) => {
    return a.value.toLowerCase() < b.value.toLowerCase() ? -1 : 1;
  });

  enabledCategories.sort((a: DropDownOption, b: DropDownOption) => {
    return a.value.toLowerCase() < b.value.toLowerCase() ? -1 : 1;
  });

  const disabledCategoriesNoDuplicates = [
    ...new Map(
      disabledCategories.map((option: DropDownOption) => [option.value, option])
    ).values(),
  ];

  const enabledCategoriesNoDuplicates = [
    ...new Map(
      enabledCategories.map((option: DropDownOption) => [option.value, option])
    ).values(),
  ];

  disabledCategoriesNoDuplicates.forEach((option: DropDownOption) => {
    option.disabled = true;
    option.description = option.description + ` ${REASON_CATEGORY_UNAVAILABLE}`;
  });

  enabledCategoriesNoDuplicates.forEach((option: DropDownOption) => {
    const index = disabledCategoriesNoDuplicates.findIndex(
      (item: DropDownOption) => item.value === option.value
    );
    if (index > -1) {
      disabledCategoriesNoDuplicates.splice(index, 1);
    }
  });

  const index = enabledCategoriesNoDuplicates.findIndex(
    (category: DropDownOption) => category.value.toLowerCase() === "other"
  );

  if (index > -1) {
    enabledCategoriesNoDuplicates.push(
      enabledCategoriesNoDuplicates.splice(index, 1)[0]
    );
  }

  return [...enabledCategoriesNoDuplicates, ...disabledCategoriesNoDuplicates];
};
